import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import '../Docs.css';

export default function SessionVsUser() {
  return (
    <div className="docs-lite">
      <Helmet>
        <link rel="canonical" href="https://www.joegalaxy.net/docs/patterns/session-vs-user" />
      </Helmet>

      <header className="docs-lite__header">
        <h1>Sessions vs Users: Modeling Presence at the Correct Level</h1>
      </header>

      <main className="docs-lite__content">
        <p>
          Presence systems fail most often because they are modeled at the <strong>user level</strong>{' '}
          instead of the <strong>session level</strong>.
        </p>

        <p>
          This document explains why sessions are the correct primitive for presence, how
          user-level presence should be derived, and how this distinction prevents flapping, false
          offline states, and coordination errors.
        </p>

        <hr />

        <section>
          <h2>Why users are the wrong primitive</h2>
          <p>
            A user is an identity.
            <br />
            Presence is behavior.
          </p>
          <p>A single user may:</p>
          <ul>
            <li>open multiple browser tabs</li>
            <li>use multiple devices simultaneously</li>
            <li>background one session while another remains active</li>
            <li>reconnect after transient network loss</li>
          </ul>
          <p>If presence is stored directly on the user, these behaviors overwrite each other.</p>
          <p>Presence becomes unstable because <strong>identity is not a runtime signal</strong>.</p>
        </section>

        <hr />

        <section>
          <h2>What a session represents</h2>
          <p>A session represents a single runtime instance:</p>
          <ul>
            <li>one browser tab</li>
            <li>one mobile app process</li>
            <li>one SDK client</li>
            <li>one connected environment</li>
          </ul>
          <p>Each session has its own:</p>
          <ul>
            <li>lifecycle</li>
            <li>heartbeat</li>
            <li>activity state</li>
            <li>expiration window</li>
          </ul>
          <p>
            Sessions are <strong>observable and time-bound</strong>.
            <br />
            Users are not.
          </p>
        </section>

        <hr />

        <section>
          <h2>Session-first presence (the correct model)</h2>
          <p>Presence must be tracked at the <strong>session level</strong>.</p>
          <p>In a session-first model:</p>
          <ul>
            <li>sessions emit signals</li>
            <li>sessions transition through lifecycle states</li>
            <li>sessions expire independently</li>
          </ul>
          <p>User presence is <strong>derived</strong> from session state, not written directly.</p>
          <p>This allows presence to remain accurate under real-world behavior.</p>
        </section>

        <hr />

        <section>
          <h2>Deriving user presence from sessions</h2>
          <p>User-level presence is computed by aggregating session states.</p>
          <p>A simple precedence model works well:</p>
          <ul>
            <li>
              <strong>active</strong> beats idle
            </li>
            <li>
              <strong>idle</strong> beats away
            </li>
            <li>
              <strong>away</strong> beats stale
            </li>
            <li>
              <strong>stale</strong> beats offline
            </li>
          </ul>
          <p>This ensures:</p>
          <ul>
            <li>an active session keeps the user active</li>
            <li>background or idle sessions do not override foreground activity</li>
            <li>closing one session does not collapse user presence</li>
          </ul>
          <p>Derivation occurs at read time, not on write.</p>
        </section>

        <hr />

        <section>
          <h2>Why derived presence matters</h2>
          <p>Derived presence provides:</p>
          <ul>
            <li>stability under rapid session changes</li>
            <li>tolerance to transient disconnects</li>
            <li>accurate availability indicators</li>
            <li>predictable coordination behavior</li>
          </ul>
          <p>Storing user-level presence as a mutable flag cannot provide these properties.</p>
        </section>

        <hr />

        <section>
          <h2>Interaction with other presence patterns</h2>
          <p>Session-first modeling integrates cleanly with:</p>
          <ul>
            <li>multi-tab presence</li>
            <li>idle detection</li>
            <li>reconnect handling</li>
            <li>grace windows</li>
            <li>lifecycle-aware state transitions</li>
          </ul>
          <p>All of these patterns assume <strong>sessions are the source of truth</strong>.</p>
        </section>

        <hr />

        <section>
          <h2>Common failure modes</h2>
          <p>Session vs user issues arise when systems:</p>
          <ul>
            <li>store <code>user.isOnline</code> directly</li>
            <li>overwrite user presence on any session change</li>
            <li>ignore multiple concurrent sessions</li>
            <li>tie presence to authentication events</li>
            <li>treat reconnects as new users</li>
          </ul>
          <p>These failures stem from <strong>identity-first modeling</strong>.</p>
        </section>

        <hr />

        <section>
          <h2>Summary</h2>
          <p>
            Presence is a runtime signal.
            <br />
            Users are identities.
          </p>
          <p>A correct presence system:</p>
          <ul>
            <li>models presence at the session level</li>
            <li>derives user presence from sessions</li>
            <li>avoids mutable user-level flags</li>
            <li>remains stable under real-world behavior</li>
          </ul>
          <p>When sessions are the primitive, presence becomes truthful and coordination-ready.</p>
        </section>

        <hr />

        <section>
          <h2>Related docs</h2>
          <ul>
            <li>
              <Link to="/docs/index.md">Docs index</Link>
            </li>
            <li>
              <Link to="/docs/presence-online-offline.md">Online vs Offline Presence</Link>
            </li>
            <li>
              <Link to="/docs/whos-online-api.md">Who’s Online API</Link>
            </li>
            <li>
              <Link to="/docs/patterns/multi-tab.md">Multi-Tab Presence</Link>
            </li>
            <li>
              <Link to="/docs/patterns/idle-detection.md">Idle Detection</Link>
            </li>
            <li>
              <Link to="/docs/patterns/reconnects.md">Reconnect Handling</Link>
            </li>
          </ul>
        </section>

        <p className="docs-lite__source">
          —
          <br />
          See this model running live:
          <br />
          <a href="https://www.joegalaxy.net/presence-demo">
            https://www.joegalaxy.net/presence-demo
          </a>
          <br />
          <a href="https://github.com/thejoegalaxy/presence-engine/blob/main/docs/patterns/session-vs-user.md">
            View source on GitHub
          </a>
        </p>
      </main>
    </div>
  );
}
